import { useState, useEffect } from 'react'
import Box from '@mui/material/Box'
import classes from './Assessment.module.scss'
import Icon from '../../../Icon'
import Button from '../../shared/ComponentLibrary/RButton'
import SelectInputField from '../../shared/ComponentLibrary/RSelectInputField'
import { SelectChangeEvent } from '@mui/material/Select';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';

type GridAreaProps = {
  innerText: string
  onClickMethod?: (event: React.MouseEvent<HTMLButtonElement>) => void
  variant?: "text" | "contained" | "outlined" | undefined
  gridArea?: string,
  mt?: number,
  align?: string,
  disabled?: boolean
}

const GridAreaButton = ({ innerText, onClickMethod, variant, gridArea, mt, align='flex-start', disabled = false }: GridAreaProps) => {
  return (
    <Box sx={{ gridArea: gridArea, visibility: onClickMethod ? 'visible' : 'hidden', mt: mt, display:'flex', justifyContent:align }}>
      <Button innerText={innerText} onClickMethod={onClickMethod} variant={variant} disabled={disabled} />
    </Box>
  )
}

const RequiredAssessment = ({ assessmentProps, enabled }: RequiredAssessmentControlProps) => {
  const { name, languages } = assessmentProps!;
  const requiredLanguages = (): Array<RequiredAssessmentSingleLanguageProps> => languages.filter(language => language.required);
  const otherLanguages = (): Array<RequiredAssessmentSingleLanguageProps> => languages.filter(language => !language.required);


  const [pickedLanguage, setPickedLanguage] = useState<string | null>(null)
  const [pickedOtherLanguage, setPickedOtherLanguage] = useState<string | null>(null)
  const [selectedOtherLanguagePractice, setSelectedOtherLanguagePractice] = useState<string | null>(null)


  const handleLanguageRadioChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setPickedLanguage((event.target as HTMLInputElement).value)
  };

  const handleOtherLanguageDropdownChange = (event: SelectChangeEvent) => {
    setPickedOtherLanguage(event.target.value as string)
    setPickedLanguage('other')
    setSelectedOtherLanguagePractice(event.target.value as string);
  };

  useEffect(() => {
    let outerRadioButtonOptions = requiredLanguages().map(language => language.val);
    if (otherLanguages().some(x => true)) {
      outerRadioButtonOptions.push('other');
    }
    if (outerRadioButtonOptions.length === 1) {
      setPickedLanguage(outerRadioButtonOptions[0]);
    }
  }, [])

  const getPickedLanguageObj = () => {
    if (pickedLanguage === null) {
      return null;
    }
    if (pickedLanguage === 'other') {
      return getPickedOtherLanguageObj();
    }
    let pickedList = languages.filter(language => language.val === pickedLanguage);
    return pickedList[0];
  }

  const getPickedOtherLanguageObj = () => {
    if (pickedOtherLanguage === null) {
      return null;
    }
    let pickedList = languages.filter(language => language.val === pickedOtherLanguage);
    return pickedList[0];
  }

  const totalStatus = requiredLanguages().every(language => Boolean(language.takenAt));

  const takenDate = (dateString: string) => new Date(dateString);

  const formattedDate = (dateString: string) => takenDate(dateString).toLocaleDateString("en-US", {
    month: "short",
    day: "2-digit",
    year: "numeric",
  });

  const formattedTime = (dateString: string) => takenDate(dateString).toLocaleTimeString("en-US", {
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  });

  return (
    <div className={classes.profile_step_container}>
      <div className={classes.step_container}>
        <div className={classes.step_non_anchor}>
          <div className={classes.step}>
            <div className={classes.step_content_frame}>
              <Icon name='page100Copy' size='28' />
              <Box className={classes.step_content}>
                <div className={classes.step_title}>{name}</div>
                <RadioGroup
                  sx={{ gridArea: 'langs' }}
                  name="languages"
                  value={pickedLanguage}
                  onChange={handleLanguageRadioChange}
                >
                  {requiredLanguages().map(language =>
                    <div className={classes.required_main_langs}>
                      <Radio
                        value={language.val}
                        sx={{gridArea: "radioButton"}}
                      />
                      <Box sx={{ gridArea: "name", alignSelf: "center" }}>
                        {language.language}
                        {language.takenAt ? ` (Taken on ${formattedDate(language.takenAt)} at ${formattedTime(language.takenAt)}) ` : ' (Required) '}
                      </Box>
                      <Box sx={{gridArea: "statusIcon", alignSelf: "center"}}>
                        {language.takenAt ?
                          <Icon name='checkmarkGreen' size='28' /> :
                          <Icon name='highPriorityFilled' size='28' />
                        }
                      </Box>
                      <Box sx={{gridArea: "statusText"}}>
                        { language.assessmentMessage ? (
                          <div>
                            <div className={classes.step_subtitle}>
                            {language.assessmentMessage}
                            </div>
                          </div>
                          ) : null
                        }
                      </Box>
                    </div>
                  )}
                  {otherLanguages().some(x => true) && <><Box className={classes.required_other_langs}>
                    <Radio
                      value='other'
                      sx={{ alignSelf: 'last baseline', gridArea: "radioButton" }}
                    />
                    <Box sx={{gridArea: "title"}}><label className={classes.label}>Other Languages</label></Box>
                    <Box sx={{gridArea: "dropdown"}}><SelectInputField
                      value=''
                      label=''
                      name='Other Languages'
                      size='parent'
                      placeholderOverride='Select Another Language'
                      onChange={handleOtherLanguageDropdownChange}
                      register={{}}
                      itemsList={otherLanguages().map(language => {
                        return {
                          name: language.language + (language.takenAt ? ` (Taken on ${formattedDate(language.takenAt)} at ${formattedTime(language.takenAt)})` : ''),
                          value: language.val
                        }
                      }
                      )}
                    /></Box>
                    
                    <Box sx={{ gridArea: "statusIcon", display: 'flex', alignItems: 'center', ml:1, mb:1 }}>
                    {
                      getPickedOtherLanguageObj() && (getPickedOtherLanguageObj()?.takenAt ?
                        <Icon name='checkmarkGreen' size='28' /> :
                        <Icon name='highPriorityFilled' size='28' />
                    )}
                  </Box>
                  {
                      getPickedOtherLanguageObj()?.assessmentMessage ? (
                        <Box className={classes.step_subtitle} sx={{mt: 1, gridArea: "statusText"}}>
                        {getPickedOtherLanguageObj()?.assessmentMessage}
                        </Box>
                      ) : null
                    }
                  </Box>
                  
                </>}
                </RadioGroup>
                <GridAreaButton innerText='Instructions' onClickMethod={getPickedLanguageObj()?.onInstructionsClickMethod} gridArea='instructions' variant='outlined' mt={1} />

                <GridAreaButton innerText='Practice Test' onClickMethod={getPickedLanguageObj()?.onPracticeTestClickMethod} gridArea='practice' variant='outlined' mt={1} align='center' />

                <GridAreaButton innerText='Start Test' onClickMethod={getPickedLanguageObj()?.onStartTestClickMethod} gridArea='start' variant='outlined' mt={1} align='flex-end' disabled={!enabled} />
              </Box>
            </div>
            <div className={classes.divider} />
            <div className={classes.total_status}>
              {
                totalStatus ?
                  <><div className={classes.total_status_icon}><Icon name='checkmarkGreen' size='28' /></div><div className={classes.total_status_description}>All requirements satisfied</div></> :
                  <><div className={classes.total_status_icon}><Icon name='highPriorityFilled' size='28' /></div><div className={classes.total_status_description}>Some requirements remaining</div></>
              }
            </div>
          </div>
        </div>
        
      </div>
    </div>
  )
}

export default RequiredAssessment
